import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RichTextDisplay } from "@/components/ui/rich-text-display";
import { Calendar, Clock, MapPin, Users, ArrowRight, Filter, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchEvents, type EventItem } from "@/services/content";
import { STALE_TIMES, queryKeys } from "@/lib/query-config";
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import '@/styles/fullcalendar.css';
import { buildEventsICS, downloadICSFile } from "@/lib/ics";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import { siteConfig } from "@/lib/site-config";
import { eventRegistrationService } from "@/services/eventRegistrationService";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const Events = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registrationData, setRegistrationData] = useState({
    name: "",
    email: "",
    phone: "",
    additional_info: "",
  });
  const [isRegistering, setIsRegistering] = useState(false);
  const [isAlreadyRegistered, setIsAlreadyRegistered] = useState(false);
  const { features } = useSiteSettings();

  const categories = ["All", "Community", "Performing Arts", "Education", "Outreach"]; 

  // Fallback static data if Supabase is not configured or returns no rows
  const staticEvents: EventItem[] = [
    {
      id: 1,
      title: "Community Celebration 2025",
      date: "2025-01-20",
      time: "2:00 PM - 6:00 PM",
      location: "Main Event Space",
      category: "Community",
      description: "Annual celebration honoring our community with performances and dialogue.",
      capacity: 200,
      registered: 45,
      featured: true
    },
    {
      id: 2,
      title: "Gospel Concert Series",
      date: "2025-02-15",
      time: "7:00 PM - 9:00 PM",
      location: "Community Performance Hall",
      category: "Performing Arts",
      description: "Powerful gospel performances featuring local and touring artists celebrating spiritual heritage.",
      capacity: 150,
      registered: 67,
      featured: true
    },
    {
      id: 3,
      title: "Financial Literacy Workshop",
      date: "2025-02-22",
      time: "10:00 AM - 12:00 PM",
      location: "Educational Center",
      category: "Education",
      description: "Essential financial skills workshop covering budgeting, saving, and building economic independence.",
      capacity: 30,
      registered: 18,
      featured: false
    },
    {
      id: 4,
      title: "Community Book Bag Drive Kickoff",
      date: "2025-03-01",
      time: "9:00 AM - 11:00 AM",
      location: "Main Lobby",
      category: "Community Outreach",
      description: "Launch event for our annual school supply drive supporting local students' educational needs.",
      capacity: 100,
      registered: 23,
      featured: false
    },
    {
      id: 5,
      title: "Women's Wellness Circle",
      date: "2025-03-08",
      time: "11:00 AM - 1:00 PM",
      location: "Wellness Room",
      category: "Education",
      description: "Holistic wellness program supporting women through health education and spiritual growth.",
      capacity: 25,
      registered: 19,
      featured: false
    },
    {
      id: 6,
      title: "Storytelling Evening",
      date: "2025-03-15",
      time: "6:30 PM - 8:30 PM",
      location: "Community Library",
      category: "Community",
      description: "Interactive evening where community elders share stories of our shared heritage and traditions.",
      capacity: 40,
      registered: 28,
      featured: true
    }
  ];

  const { data: eventsData, isLoading } = useQuery({
    queryKey: queryKeys.events.list(),
    queryFn: async () => {
      const result = await fetchEvents();
      return result.success ? result.data : [];
    },
    staleTime: STALE_TIMES.events,
  });

  const eventsToUse: EventItem[] = (eventsData && eventsData.length > 0) ? eventsData : staticEvents;

  const filteredEvents = eventsToUse.filter(event => {
    const matchesCategory = selectedCategory === "All" || event.category === selectedCategory;
    const matchesSearch = event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const calendarEvents = filteredEvents.map((e) => ({
    id: String(e.id),
    title: e.title,
    start: e.date, // Using date-only; time shown in list view
    extendedProps: {
      category: e.category,
      time: e.time,
      location: e.location,
    },
  }));

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "Community": return "text-heritage bg-heritage/10";
      case "Performing Arts": return "text-culture bg-culture/10";
      case "Education": return "text-accent bg-accent/10";
      case "Community Outreach": return "text-primary bg-primary/10";
      default: return "text-muted-foreground bg-muted";
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleDownloadICS = () => {
    const ics = buildEventsICS(filteredEvents);
    downloadICSFile("ifi-heritage-events.ics", ics);
  };

  // Custom renderer for calendar events
  const renderEventContent = (eventInfo: any) => {
    const event = filteredEvents.find(e => e.id.toString() === eventInfo.event.id);
    const categoryColor = event ? getCategoryColor(event.category).split(' ')[0] : 'bg-primary';
    
    return (
      <div className={`p-1 text-xs truncate ${categoryColor}/10`}>
        <div className="font-medium truncate">{eventInfo.event.title}</div>
        {event?.time && (
          <div className="text-xs opacity-80">
            {event.time.split(' - ')[0]} {/* Show start time only */}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-warm">
      <SEO
        title="Events"
        description="Discover upcoming community events, workshops, and gatherings. Join us for educational programs, celebrations, and community impact initiatives."
        keywords={['events', 'community', 'calendar', 'programs', 'workshops']}
        type="website"
      />
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            Our <span className="text-heritage">Events & Programs</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Join us for enriching events, educational workshops, and community 
            celebrations that strengthen our bonds.
          </p>
        </div>
      </section>

      {/* Filters and Search */}
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 mb-8">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search events..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            {/* Category Filter */}
            <div className="flex gap-2 flex-wrap">
              <Filter className="w-5 h-5 text-muted-foreground mt-2" />
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "heritage" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </Button>
              ))}
            </div>

            {/* Download ICS */}
            <div className="flex items-center">
              <Button variant="outline" onClick={handleDownloadICS}>
                Download .ics
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Calendar View */}
      <section className="py-4 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {isLoading ? (
            <div className="text-center py-8">Loading calendar...</div>
          ) : (
            <div className="border rounded-lg p-4 bg-card">
              <FullCalendar
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: ''
                }}
                height="auto"
                dayMaxEvents={3}
                events={calendarEvents}
                eventContent={renderEventContent}
                dayHeaderFormat={{ weekday: 'short' }}
                dayHeaderClassNames="text-foreground/80 text-sm font-medium"
                dayCellClassNames="hover:bg-muted/50 transition-colors"
                eventClassNames="cursor-pointer hover:opacity-90"
                eventTimeFormat={{
                  hour: 'numeric',
                  minute: '2-digit',
                  meridiem: 'short',
                  hour12: true
                }}
                views={{
                  dayGrid: {
                    dayMaxEventRows: 3,
                    dayHeaderFormat: { weekday: 'short' },
                  },
                }}
              />
            </div>
          )}
        </div>
      </section>

      {/* Events List */}
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {isLoading ? (
            <div className="text-center py-12">Loading events...</div>
          ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {filteredEvents.map((event) => (
              <Card 
                key={event.id} 
                className={`shadow-warm hover:shadow-heritage transition-all duration-300 ${
                  event.featured ? 'ring-2 ring-heritage/20 shadow-heritage' : ''
                }`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-3 ${getCategoryColor(event.category)}`}>
                        {event.category}
                      </span>
                      <CardTitle className="text-xl mb-2">{event.title}</CardTitle>
                    </div>
                    {event.featured && (
                      <div className="bg-heritage text-heritage-foreground px-2 py-1 rounded-full text-xs font-medium">
                        Featured
                      </div>
                    )}
                  </div>
                </CardHeader>
                
                <CardContent>
                  <div className="text-muted-foreground mb-6 line-clamp-3">
                    <RichTextDisplay content={event.description || ''} />
                  </div>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center text-sm text-muted-foreground">
                      <Calendar className="w-4 h-4 mr-2 text-heritage" />
                      {formatDate(event.date)}
                    </div>
                    <div className="flex items-center text-sm text-muted-foreground">
                      <Clock className="w-4 h-4 mr-2 text-heritage" />
                      {event.time}
                    </div>
                    <div className="flex items-center text-sm text-muted-foreground">
                      <MapPin className="w-4 h-4 mr-2 text-heritage" />
                      {event.location}
                    </div>
                    <div className="flex items-center text-sm text-muted-foreground">
                      <Users className="w-4 h-4 mr-2 text-heritage" />
                      {event.registered} / {event.capacity} registered
                    </div>
                  </div>
                  
                  {/* Registration Progress */}
                  <div className="mb-6">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-muted-foreground">Registration</span>
                      <span className="text-heritage font-medium">
                        {Math.round((event.registered / event.capacity) * 100)}% full
                      </span>
                    </div>
                    <div className="w-full bg-secondary rounded-full h-2">
                      <div 
                        className="bg-gradient-heritage h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(event.registered / event.capacity) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <Button 
                      variant="heritage" 
                      className="flex-1"
                      disabled={event.registered >= event.capacity}
                      onClick={async () => {
                        setSelectedEvent(event);
                        setRegistrationData({ name: "", email: "", phone: "", additional_info: "" });
                        setIsAlreadyRegistered(false);
                        setShowRegisterModal(true);
                        
                        // Check if user is already registered (if they have email in localStorage or similar)
                        // For now, we'll check after they enter email
                      }}
                    >
                      {event.registered >= event.capacity ? "Full" : "Register Now"}
                      {event.registered < event.capacity && <ArrowRight className="w-4 h-4 ml-2" />}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setSelectedEvent(event);
                        setShowDetailsModal(true);
                      }}
                    >
                      Details
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          )}

          {filteredEvents.length === 0 && (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-medium text-foreground mb-2">No events found</h3>
              <p className="text-muted-foreground">
                Try adjusting your search or category filter to find events.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-secondary/30">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-foreground mb-6">
            Stay Connected to Your Community
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            Get notified about upcoming events, workshops, and community celebrations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto mb-8">
            <Input 
              placeholder="Enter your email address" 
              className="flex-1"
            />
            <Button variant="heritage">
              Subscribe
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          {(features.membership.enabled || features.donation.enabled) && (
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {features.membership.enabled && (
                <Button variant="culture" size="lg" asChild>
                  <Link to="/membership">
                    <Users className="w-5 h-5" />
                    Become a Member
                  </Link>
                </Button>
              )}
              {features.donation.enabled && (
                <Button variant="donate" size="lg" asChild>
                  <Link to="/donate">
                    Support Our Events
                  </Link>
                </Button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Event Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">{selectedEvent?.title}</DialogTitle>
            <DialogDescription>
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mt-2 ${getCategoryColor(selectedEvent?.category || '')}`}>
                {selectedEvent?.category}
              </span>
            </DialogDescription>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-6">
              <div className="text-muted-foreground">
                <RichTextDisplay content={selectedEvent.description || ''} />
              </div>
              
              <div className="space-y-3 p-4 bg-secondary/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-heritage" />
                  <span>{formatDate(selectedEvent.date)}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-heritage" />
                  <span>{selectedEvent.time}</span>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-heritage" />
                  <span>{selectedEvent.location}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-heritage" />
                  <span>{selectedEvent.registered} / {selectedEvent.capacity} registered ({Math.round((selectedEvent.registered / selectedEvent.capacity) * 100)}% full)</span>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  variant="heritage" 
                  className="flex-1"
                  disabled={selectedEvent.registered >= selectedEvent.capacity}
                  onClick={() => {
                    setShowDetailsModal(false);
                    setShowRegisterModal(true);
                  }}
                >
                  {selectedEvent.registered >= selectedEvent.capacity ? "Event Full" : "Register for This Event"}
                </Button>
                <Button variant="outline" onClick={() => setShowDetailsModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Registration Modal */}
      <Dialog open={showRegisterModal} onOpenChange={setShowRegisterModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Register for Event</DialogTitle>
            <DialogDescription>
              {selectedEvent?.title}
            </DialogDescription>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4">
              <div className="p-4 bg-secondary/30 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                  <Calendar className="w-4 h-4" />
                  {formatDate(selectedEvent.date)} at {selectedEvent.time}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="w-4 h-4" />
                  {selectedEvent.location}
                </div>
              </div>
              
              {isAlreadyRegistered ? (
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                  <p className="text-green-700 dark:text-green-300 font-medium mb-2">
                    You're already registered for this event!
                  </p>
                  <p className="text-sm text-green-600 dark:text-green-400">
                    Check your email for confirmation details.
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="reg-name">Full Name *</Label>
                      <Input
                        id="reg-name"
                        value={registrationData.name}
                        onChange={(e) => setRegistrationData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Your name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="reg-email">Email Address *</Label>
                      <Input
                        id="reg-email"
                        type="email"
                        value={registrationData.email}
                        onChange={(e) => setRegistrationData(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="reg-phone">Phone Number</Label>
                      <Input
                        id="reg-phone"
                        type="tel"
                        value={registrationData.phone}
                        onChange={(e) => setRegistrationData(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <Label htmlFor="reg-additional">Additional Information (Optional)</Label>
                      <Textarea
                        id="reg-additional"
                        value={registrationData.additional_info}
                        onChange={(e) => setRegistrationData(prev => ({ ...prev, additional_info: e.target.value }))}
                        placeholder="Dietary restrictions, accessibility needs, etc."
                        rows={3}
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="heritage"
                      className="flex-1"
                      onClick={async () => {
                        if (!selectedEvent) return;
                        
                        if (!registrationData.name || !registrationData.email) {
                          toast.error("Please fill in all required fields");
                          return;
                        }

                        setIsRegistering(true);
                        try {
                          // Check if already registered
                          const checkResult = await eventRegistrationService.checkRegistration(
                            selectedEvent.id,
                            registrationData.email
                          );

                          if (checkResult.success && checkResult.data) {
                            setIsAlreadyRegistered(true);
                            toast.info("You're already registered for this event");
                            setIsRegistering(false);
                            return;
                          }

                          const result = await eventRegistrationService.registerForEvent(
                            selectedEvent.id,
                            registrationData
                          );

                          if (result.success) {
                            toast.success(`Registration successful! Check your email for confirmation.`);
                            setShowRegisterModal(false);
                            setSelectedEvent(null);
                            setRegistrationData({ name: "", email: "", phone: "", additional_info: "" });
                            setIsAlreadyRegistered(false);
                            // Refresh events to update registered count
                            window.location.reload();
                          } else {
                            toast.error(result.error.getUserMessage() || "Failed to register. Please try again.");
                          }
                        } catch (error) {
                          console.error('Error registering for event:', error);
                          toast.error("An unexpected error occurred. Please try again.");
                        } finally {
                          setIsRegistering(false);
                        }
                      }}
                      disabled={isRegistering}
                    >
                      {isRegistering ? "Registering..." : "Complete Registration"}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowRegisterModal(false);
                        setRegistrationData({ name: "", email: "", phone: "", additional_info: "" });
                        setIsAlreadyRegistered(false);
                      }}
                      disabled={isRegistering}
                    >
                      Cancel
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Events;